import React, { Component } from 'react';
import { AgGridReact } from 'ag-grid-react';
import moment from 'moment';
import toastr from 'toastr';
import { Button, Modal } from 'react-bootstrap';
import { ThemeContext } from '../../../../theme/ThemeProvider';
import { colorSchemeDark, colorSchemeLight, themeQuartz } from 'ag-grid-community';
import { getAllPatientMedications, getPatientMedications, discontinuePatientMedication, deletePatientMedication } from '../../../../services/clinicPortalServices/patientMedicationService';
import { getUserRole, getUserDetails } from '../../../../services/common/util';
import EditBtnCellRenderer from './editBtnCellRenderer';
import StatusCellRenderer from './statusCellRenderer';
import PatientMedicationSearchMenu from './patientMedicationSearchMenu';
import PatientMedicationBreadcrumb from './patientMedicationBreadcrumb';
import AddPatientMedication from './addPatientMedication';
import { ModalStyled } from '../../../../theme/customizedStyleComponents';
import PatientMedicationDetails from './patientMedicationDetails';

const themeLight = themeQuartz.withPart(colorSchemeLight);
const themeDark = themeQuartz.withPart(colorSchemeDark);

export default class PatientMedicationGrid extends Component {
    constructor(props) {
        super(props);
        this.state = {
            quickFilterText: '',
            paginationPageSize: 20,
            columnDefs: [
                {
                    headerName: 'Actions',
                    minWidth: 100,
                    maxWidth: 100,
                    cellStyle: { textAlign: 'center' },
                    cellRenderer: 'editBtnCellRenderer',
                    pinned: 'left'
                },
                {
                    headerName: 'Status',
                    field: 'status',
                    minWidth: 120,
                    cellRenderer: 'statusCellRenderer'
                },
                {
                    headerName: 'Patient',
                    field: 'patientId.full_name',
                    minWidth: 150,
                    valueGetter: (params) => {
                        if (params.data && params.data.patientId) {
                            const patient = params.data.patientId;
                            const firstName = patient.first_name || '';
                            const lastName = patient.last_name || '';
                            return `${firstName} ${lastName}`.trim() || 'Unknown Patient';
                        }
                        return 'Unknown Patient';
                    }
                },
                {
                    headerName: 'MRN',
                    field: 'patientId.mrn',
                    minWidth: 100
                },
                {
                    headerName: 'Medication',
                    field: 'medicationId.name',
                    minWidth: 200
                },
                {
                    headerName: 'Dosage',
                    field: 'dosageDisplay',
                    minWidth: 120,
                    valueGetter: (params) => {
                        return `${params.data.dosage} ${params.data.dosageUnit}`;
                    }
                },
                {
                    headerName: 'Frequency',
                    field: 'frequency',
                    minWidth: 120
                },
                {
                    headerName: 'Route',
                    field: 'route',
                    minWidth: 100
                },
                {
                    headerName: 'Start Date',
                    field: 'startDate',
                    minWidth: 120,
                    valueGetter: (params) => {
                        return params.data.startDate ? moment(params.data.startDate).format('MM/DD/YYYY') : '';
                    }
                },
                {
                    headerName: 'End Date',
                    field: 'endDate',
                    minWidth: 120,
                    valueGetter: (params) => {
                        return params.data.endDate ? moment(params.data.endDate).format('MM/DD/YYYY') : '';
                    }
                },
                {
                    headerName: 'Prescriber',
                    field: 'prescriber.name',
                    minWidth: 150
                },
                {
                    headerName: 'Last Dose',
                    field: 'lastDoseTime',
                    minWidth: 150,
                    valueGetter: (params) => {
                        return params.data.lastDoseTime
                            ? moment(params.data.lastDoseTime).format('MM/DD/YYYY HH:mm')
                            : '';
                    }
                },
                {
                    headerName: 'Verified',
                    field: 'isVerified',
                    minWidth: 80,
                    valueGetter: (params) => {
                        return params.data.isVerified ? 'Yes' : 'No';
                    }
                },
                {
                    headerName: 'Verified By',
                    field: 'verifiedBy.user_name',
                    minWidth: 120
                },
                {
                    headerName: 'Created Date',
                    field: 'createdAt',
                    minWidth: 150,
                    valueGetter: (params) => {
                        return params.data.createdAt ? moment(params.data.createdAt).format('MM/DD/YYYY HH:mm') : '';
                    }
                }
            ],
            components: {
                editBtnCellRenderer: EditBtnCellRenderer,
                statusCellRenderer: StatusCellRenderer
            },
            defaultColDef: {
                flex: 1,
                filter: true,
                sortable: true,
                resizable: true,
                enableRowGroup: true,
                enablePivot: true,
                enableValue: true
            },
            rowData: [],
            context: { componentParent: this },
            showAddModal: false,
            showEditModal: false,
            showDiscontinueModal: false,
            showArchiveModal: false,
            confirmMedication: null,
            discontinueReason: '',
            selectedMedication: null,
            selectedPatientId: null,
            isAdminView: false,
            pageSize: 20,
            paginationNumberFormatter: function (params) {
                return '[' + params.value.toLocaleString() + ']';
            }
        };
    }

    componentDidMount() {
        // Check if we're in admin view or patient-specific view
        const patientId = this.props.match?.params?.patientId || this.props.patientId;
        const isAdminView = !patientId;

        this.setState({
            selectedPatientId: patientId,
            isAdminView
        });

        if (patientId) {
            this.loadPatientMedications(patientId);
        } else {
            this.loadAllPatientMedications();
        }
    }

    onGridReady = (params) => {
        this.gridApi = params.api;
        this.gridColumnApi = params.columnApi;

        // Auto-size columns to fit
        params.api.sizeColumnsToFit();
    };

    loadAllPatientMedications = () => {
        getAllPatientMedications()
            .then((response) => {
                if (response.status === 200) {
                    this.setState({ rowData: response.data });
                } else {
                    toastr.error('Error loading patient medications');
                }
            })
            .catch((error) => {
                console.error('Error loading all medications:', error);
                toastr.error('Error loading patient medications');
            });
    };

    loadPatientMedications = (patientId) => {
        if (!patientId) {
            toastr.error('Patient ID is required');
            return;
        }

        getPatientMedications(patientId)
            .then((response) => {
                if (response.status === 200) {
                    this.setState({ rowData: response.data });
                } else {
                    toastr.error('Error loading patient medications');
                }
            })
            .catch((error) => {
                console.error('Error loading medications:', error);
                toastr.error('Error loading patient medications');
            });
    };

    refreshGrid = () => {
        if (this.state.selectedPatientId) {
            this.loadPatientMedications(this.state.selectedPatientId);
        } else {
            this.loadAllPatientMedications();
        }
    };

    clearFilter = () => {
        this.gridApi.setFilterModel(null);
        this.gridApi.onFilterChanged();
        this.setState({ quickFilterText: '' });
    };

    onBtnExport = () => {
        const fileName = `PatientMedications_${moment().format('YYYY_MM_DD_HH_mm_ss')}.xlsx`;
        this.gridApi.exportDataAsExcel({
            fileName: fileName,
            sheetName: 'Patient Medications'
        });

        // Audit log for export
        const userDetails = JSON.parse(getUserDetails());
        console.log('Export triggered by:', userDetails.user_name);
    };

    onQuickFilterChanged = (value) => {
        this.setState({ quickFilterText: value });
    };

    onPageSizeChanged = (newPageSize) => {
        this.setState({ paginationPageSize: Number(newPageSize) });
    };

    handleAddMedication = () => {
        this.setState({ showAddModal: true });
    };

    handleCloseAddModal = () => {
        this.setState({ showAddModal: false });
    };

    handleEditMedication = (medication) => {
        this.setState({
            selectedMedication: medication,
            showEditModal: true
        });
    };

    handleCloseEditModal = () => {
        this.setState({
            selectedMedication: null,
            showEditModal: false
        });
    };

    handleDiscontinueMedication = (medication) => {
        this.setState({ confirmMedication: medication, discontinueReason: '', showDiscontinueModal: true });
    };

    confirmDiscontinue = () => {
        const { confirmMedication, discontinueReason } = this.state;
        if (!discontinueReason.trim()) {
            toastr.error('Please provide a reason for discontinuation');
            return;
        }
        discontinuePatientMedication(confirmMedication._id, { reason: discontinueReason })
            .then(() => {
                toastr.success('Medication discontinued');
                this.setState({ showDiscontinueModal: false, confirmMedication: null });
                this.refreshGrid();
            })
            .catch(() => toastr.error('Error discontinuing medication'));
    };

    handleArchiveMedication = (medication) => {
        this.setState({ confirmMedication: medication, showArchiveModal: true });
    };

    confirmArchive = () => {
        const { confirmMedication } = this.state;
        deletePatientMedication(confirmMedication._id)
            .then(() => {
                toastr.success('Medication archived');
                this.setState({ showArchiveModal: false, confirmMedication: null });
                this.refreshGrid();
            })
            .catch(() => toastr.error('Error archiving medication'));
    };

    updateGridRow = (nodeId, updatedData) => {
        const rowNode = this.gridApi.getRowNode(nodeId);
        if (rowNode) {
            rowNode.setData(updatedData);
        }
    };

    render() {
        const userRole = getUserRole();
        const canEdit = !/audit|salesperson/.test(userRole.toLowerCase());

        // Filter columns based on role
        let columnDefs = canEdit
            ? this.state.columnDefs
            : this.state.columnDefs.filter((x) => x.headerName !== 'Actions');

        return (
            <div className="clinic-contain">
                <PatientMedicationBreadcrumb
                    patientId={this.state.selectedPatientId}
                    isAdminView={this.state.isAdminView}
                />
                <div className="p-3">
                    <div className="row mb-3">
                        <div className="col-12">
                            <PatientMedicationSearchMenu
                                onQuickFilterChanged={this.onQuickFilterChanged}
                                onClearFilter={this.clearFilter}
                                onExport={this.onBtnExport}
                                onPageSizeChanged={this.onPageSizeChanged}
                                pageSize={this.state.pageSize}
                                quickFilterText={this.state.quickFilterText}
                                patientId={this.state.selectedPatientId}
                                onPatientSelected={(patientId) => {
                                    this.setState({ selectedPatientId: patientId });
                                    this.loadPatientMedications(patientId);
                                }}
                                onAddMedication={this.handleAddMedication}
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <ThemeContext.Consumer>
                                {({ themeName }) => (
                                    <div className="ag-theme-alpine" style={{ height: '600px', width: '100%' }}>
                                        <AgGridReact
                                            columnDefs={columnDefs}
                                            defaultColDef={this.state.defaultColDef}
                                            onGridReady={this.onGridReady}
                                            rowData={this.state.rowData}
                                            pagination={true}
                                            paginationPageSize={this.state.paginationPageSize}
                                            paginationNumberFormatter={this.state.paginationNumberFormatter}
                                            quickFilterText={this.state.quickFilterText}
                                            context={this.state.context}
                                            components={this.state.components}
                                            theme={themeName === 'Light' ? themeLight : themeDark}
                                            suppressRowClickSelection={true}
                                            rowSelection="single"
                                        />
                                    </div>
                                )}
                            </ThemeContext.Consumer>
                        </div>
                    </div>
                </div>

                {/* Add Medication Modal */}
                <AddPatientMedication
                    show={this.state.showAddModal}
                    handleClose={this.handleCloseAddModal}
                    patientId={this.state.selectedPatientId}
                    refreshGrid={this.refreshGrid}
                />

                {/* Edit Medication Modal */}
                {this.state.showEditModal && (
                    <ModalStyled
                        size="lg"
                        show={this.state.showEditModal}
                        onHide={this.handleCloseEditModal}
                        backdrop="static"
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>Edit Patient Medication</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <PatientMedicationDetails
                                medication={this.state.selectedMedication}
                                handleClose={this.handleCloseEditModal}
                                refreshGrid={this.refreshGrid}
                                context={this.state.context}
                                hideFooter={true}
                            />
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={this.handleCloseEditModal}>
                                Cancel
                            </Button>
                            {(this.state.selectedMedication?.status === 'active' || !this.state.selectedMedication?._id) && (
                                <Button 
                                    variant="primary" 
                                    onClick={() => {
                                        // Trigger form submission in the child component
                                        const form = document.querySelector('.medication-details-form');
                                        if (form) {
                                            form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                                        }
                                    }}
                                >
                                    Update Medication
                                </Button>
                            )}
                        </Modal.Footer>
                    </ModalStyled>
                )}

                {/* Discontinue Confirmation Modal */}
                <ModalStyled
                    size="md"
                    show={this.state.showDiscontinueModal}
                    onHide={() => this.setState({ showDiscontinueModal: false, confirmMedication: null })}
                    backdrop="static"
                >
                    <Modal.Header closeButton>
                        <Modal.Title><i className="fas fa-stop-circle text-danger mr-2"></i>Discontinue Medication</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>
                            You are about to discontinue <strong>{this.state.confirmMedication?.medicationId?.name || 'this medication'}</strong>
                            {this.state.confirmMedication?.patientId &&
                                <> for <strong>{this.state.confirmMedication.patientId.first_name} {this.state.confirmMedication.patientId.last_name}</strong></>
                            }.
                        </p>
                        <div className="form-group">
                            <label className="font-weight-bold">Reason for Discontinuation <span className="text-danger">*</span></label>
                            <textarea
                                className="form-control"
                                rows={3}
                                placeholder="Please provide a reason..."
                                value={this.state.discontinueReason}
                                onChange={(e) => this.setState({ discontinueReason: e.target.value })}
                            />
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => this.setState({ showDiscontinueModal: false, confirmMedication: null })}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={this.confirmDiscontinue} disabled={!this.state.discontinueReason.trim()}>
                            <i className="fas fa-stop-circle mr-1"></i>Discontinue
                        </Button>
                    </Modal.Footer>
                </ModalStyled>

                {/* Archive Confirmation Modal */}
                <ModalStyled
                    size="md"
                    show={this.state.showArchiveModal}
                    onHide={() => this.setState({ showArchiveModal: false, confirmMedication: null })}
                    backdrop="static"
                >
                    <Modal.Header closeButton>
                        <Modal.Title><i className="fas fa-archive text-warning mr-2"></i>Archive Medication</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>
                            Are you sure you want to archive <strong>{this.state.confirmMedication?.medicationId?.name || 'this medication'}</strong>
                            {this.state.confirmMedication?.patientId &&
                                <> for <strong>{this.state.confirmMedication.patientId.first_name} {this.state.confirmMedication.patientId.last_name}</strong></>
                            }?
                        </p>
                        <p className="text-muted mb-0">Archived medications are hidden from the active view but are retained for record-keeping purposes.</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => this.setState({ showArchiveModal: false, confirmMedication: null })}>
                            Cancel
                        </Button>
                        <Button variant="warning" onClick={this.confirmArchive}>
                            <i className="fas fa-archive mr-1"></i>Archive
                        </Button>
                    </Modal.Footer>
                </ModalStyled>
            </div>
        );
    }
}
